import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { SIMILAR_PROJECTS_PAGE_SIZE } from '@modules/project/util/similar-projects-config';
import { LOAD_MORE_SIMILAR_PROJECTS_ACTION } from '~/routes/actions.load-more-similar-projects';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';

interface LoadMoreResponse {
    projects: ProjectTileViewModel[];
    hasMore: boolean;
    totalCount: number;
}

export interface SimilarProjects {
    /** Trimmed to complete grid rows while more pages remain. */
    tiles: DetailTile[];
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
}

/**
 * Data for the project page's "Similar Projects" grid. The first page is SSR'd
 * by the project loader (`similarProjects`); this hook holds the later pages
 * appended via "Load More" (`/actions/load-more-similar-projects`) and merges
 * them — the same shape as the product page's `useSimilarProducts`.
 *
 * Its paging state is per-project and is reset by remounting: the section that
 * calls it is keyed on the project id. Project-to-project navigation does not
 * remount the page, and the commonest way to leave this grid is clicking one
 * of its own tiles — which lands on another project in the SAME category, so
 * anything keyed on the category would not notice the move at all.
 */
export const useSimilarProjects = (
    /** The grid's desktop column count, so the trim keeps whole rows of it. */
    desktopColumns = 3,
): SimilarProjects => {
    const { project, similarProjects } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const pageSize = mobile
        ? SIMILAR_PROJECTS_PAGE_SIZE.mobile
        : SIMILAR_PROJECTS_PAGE_SIZE.desktop;
    const columns = mobile ? 2 : desktopColumns;

    // Page 0 from the loader (SSR); Remix serialises it to JsonifyObject.
    const initialProjects = useMemo(
        () =>
            (similarProjects?.projects ??
                []) as unknown as ProjectTileViewModel[],
        [similarProjects],
    );

    const fetcher = useFetcher<LoadMoreResponse>();
    // Only the "Load More" pages live in state; page 0 comes from the loader.
    const [appended, setAppended] = useState<ProjectTileViewModel[]>([]);
    const [serverHasMore, setServerHasMore] = useState<boolean | null>(null);
    const nextOffsetRef = useRef<number>(pageSize);
    const requestedOffsetRef = useRef(-1);
    const processedOffsetRef = useRef(-1);

    const categoryLink =
        project.Categories?.[0]?.Link?.replace(/\/+$/, '') ?? '';

    const fetchPage = useCallback(
        (offset: number) => {
            if (!categoryLink || requestedOffsetRef.current === offset) return;
            requestedOffsetRef.current = offset;
            const params = new URLSearchParams({
                categoryLink,
                currentProjectId: String(project.ID),
                offset: String(offset),
                limit: String(pageSize),
            });
            fetcher.load(
                `${LOAD_MORE_SIMILAR_PROJECTS_ACTION}?${params.toString()}`,
            );
        },
        [categoryLink, fetcher, pageSize, project.ID],
    );

    // Append each "Load More" response once, excluding anything already loaded
    // (loader page 0 + earlier appended pages).
    useEffect(() => {
        if (fetcher.state !== 'idle') return;
        if (processedOffsetRef.current === requestedOffsetRef.current) return;
        if (!fetcher.data) {
            // Settled with no payload — a network failure, or the route's
            // fail-soft empty. `fetchPage` refuses an offset it has already
            // asked for, and the next offset only advances on a successful
            // page, so leaving the guard set would retire Load More for the
            // rest of the session with no way back.
            requestedOffsetRef.current = -1;
            return;
        }
        processedOffsetRef.current = requestedOffsetRef.current;

        const incoming = (fetcher.data.projects ??
            []) as unknown as ProjectTileViewModel[];
        setAppended((prev) => {
            const seen = new Set(
                [...initialProjects, ...prev].map((tile) => tile.id),
            );
            const fresh = incoming.filter((tile) => !seen.has(tile.id));
            return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerHasMore(Boolean(fetcher.data.hasMore));
        nextOffsetRef.current = requestedOffsetRef.current + pageSize;
    }, [fetcher.state, fetcher.data, initialProjects, pageSize]);

    // Stable across renders (only fetchPage's deps move it) so consumers that
    // memoise on it don't thrash.
    const loadMore = useCallback(
        () => fetchPage(nextOffsetRef.current),
        [fetchPage],
    );

    const items = useMemo(
        () => [...initialProjects, ...appended],
        [initialProjects, appended],
    );

    const allTiles: DetailTile[] = items
        .map((model) => ({
            id: model.id,
            link: model.link,
            title: model.title,
            images: model.images,
            subtitle: model.professional?.name,
            professional: {
                id: model.professional?.id,
                name: model.professional?.name,
            },
            professionalLink: model.professional?.link,
            // The item's own site, which arms the hover panel's Visit site.
            website: model.website,
            pinType: 'ProjectPin' as const,
        }))
        // The server excludes the project being read, but only for the id it
        // was given; filtering here too means the page can never list itself.
        .filter((tile) => tile.id !== project.ID && tile.images?.[0]?.src);

    const hasMore = serverHasMore ?? Boolean(similarProjects?.hasMore);

    // While more pages remain, show only complete rows (3 cols desktop / 2
    // mobile) so the current-project exclusion or a short page never leaves a
    // ragged row; the trimmed tiles fill in on the next "Load More".
    const fullRowCount = Math.floor(allTiles.length / columns) * columns;
    const tiles =
        hasMore && fullRowCount >= columns
            ? allTiles.slice(0, fullRowCount)
            : allTiles;

    return {
        tiles,
        hasMore,
        isLoading: fetcher.state !== 'idle',
        loadMore,
    };
};
